import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import {
  ShieldAlert,
  BarChart3,
  SearchCheck,
  Mail,
  Handshake,
  type LucideIcon,
} from 'lucide-react';

interface ActionEntry {
  id: string;
  agentId: string;
  status: string;
  confidence: number | null;
  reasoning: string | null;
  createdAt: string | null;
}

const AGENT_DISPLAY: Record<string, { name: string; icon: LucideIcon; color: string }> = {
  A2_LEAD_SCORING: { name: 'Lead Score', icon: BarChart3, color: 'text-blue-600' },
  A4_GAP_DETECTION: { name: 'Gap Analysis', icon: SearchCheck, color: 'text-amber-600' },
  A5_RISK_SCORING: { name: 'Risk Score', icon: ShieldAlert, color: 'text-red-600' },
  A6_FOLLOW_UP_DRAFTING: { name: 'Follow-Up Draft', icon: Mail, color: 'text-emerald-600' },
  A8_ONBOARDING_BOT: { name: 'Onboarding', icon: Handshake, color: 'text-purple-600' },
};

interface Props {
  targetType: string;
  targetId: string;
}

/**
 * Story 23.6 — surfaces agent results inline on entity pages.
 * Shows the most recent successful output per agent type as a
 * compact insight card.
 */
export function AgentInsights({ targetType, targetId }: Props) {
  const [entries, setEntries] = useState<ActionEntry[]>([]);

  useEffect(() => {
    apiClient
      .get<ActionEntry[]>('/agents/log', {
        params: { targetType, targetId },
      })
      .then(({ data }) => setEntries(data.filter((e) => e.status === 'SUCCESS')))
      .catch(() => setEntries([]));
  }, [targetType, targetId]);

  // Deduplicate: keep only the most recent per agent
  const byAgent = new Map<string, ActionEntry>();
  for (const entry of entries) {
    if (!byAgent.has(entry.agentId)) {
      byAgent.set(entry.agentId, entry);
    }
  }

  const insights = Array.from(byAgent.values()).filter(
    (e) => AGENT_DISPLAY[e.agentId],
  );

  if (insights.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        AI Insights
      </h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {insights.map((entry) => {
          const display = AGENT_DISPLAY[entry.agentId]!;
          const Icon = display.icon;
          return (
            <div
              key={entry.id}
              className="flex items-start gap-2 rounded-lg border bg-muted/50 p-3"
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${display.color}`} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold">{display.name}</span>
                  {entry.confidence !== null && (
                    <span className="text-[10px] text-muted-foreground">
                      {entry.confidence}%
                    </span>
                  )}
                </div>
                {entry.reasoning && (
                  <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                    {entry.reasoning}
                  </p>
                )}
                {entry.createdAt && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {new Date(entry.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
